import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { ocppService } from '../services/ocpp/OcppService';
import { userService } from '../services/user/UserService';
import { roleService } from '../services/role/RoleService';
import { organizationService } from '../services/organization/OrganizationService';
import {
  AssignRolesPayload,
  CreateRolePayload,
  DeleteRoleOptions,
  UpdateRolePayload,
} from '../types/role';
import {
  CreateOrganizationPayload,
  OwnerCandidate,
  UpdateOrganizationPayload,
} from '../types/organization';
import { OWNER_SEARCH_PAGE_SIZE } from '../constants/organization';
import { parseApiError } from '../utils/parseApiError';
import { getDateRangeFromPreset } from '../shared/utils/dateRangeUtils';
import { toStationStatus, toTransactionStatus, toSessionStatus, toUserStatus, toUserRole, toConnectorStatus } from '../shared/utils/typeGuards';
import { ConnectorStatus } from '../shared/enums/status';
import type { ConnectorTypeEnum } from '../shared/enums/connector-type';
import type { StationData } from '../pages/DashboardV2/StationDetails/StationDetails';
import {
  StationV2,
  TransactionV2,
  SessionV2,
  UserV2,
  UserDetailsV2,
  BalanceUpdatePayload,
  StationsFilterState,
  TransactionsFilterState,
  SessionsFilterState,
  UsersFilterState,
  StationRequestV2,
  StationRequestsFilterState,
} from '../types/dashboardV2';
import { DashboardQueryKey } from '../constants/dashboardQueryKeys';
import type { SessionType } from '../components/common/Card/UserSessionCard/types';
import type { TransactionType } from '../components/common/Card/UserTransactionCard/types';

/**
 * How often the admin sessions list re-reads the backend. Nothing pushes
 * session events to the dashboard — there is no socket under DashboardV2 — so
 * a session that starts, finishes or changes status while the page is open is
 * invisible until something refetches. 15s keeps the live view honest at one
 * request per open tab, and polling pauses while the tab is hidden.
 */
export const SESSIONS_POLL_INTERVAL_MS = 15_000;

function mapSession(s: SessionType): SessionV2 {
  return {
    id: s._id,
    sessionId: s.sessionId,
    idTag: s.idTag,
    connectorId: s.connectorId,
    chargingStatus: toSessionStatus(String(s.chargingStatus)),
    amount: s.amount / 100,
    // Written only by a settle path, so presence means "settled".
    settledAmount: s.settledAmount != null ? s.settledAmount / 100 : undefined,
    energy: ((s.meterValue ?? 0) - (s.meterStart ?? 0)) / 1000,
    totalQuantity: s.meterStop / 1000,
    chargingTime: s.chargingTime,
    userId: s.userId,
    createdAt: s.createdAt,
    startedBalance: s.startedBalance != null ? s.startedBalance / 100 : undefined,
    stationPrice: (s as unknown as { stationPrice?: number }).stationPrice ?? undefined,
    premiumPricePerKwh:
      (s as unknown as { premiumPricePerKwh?: number }).premiumPricePerKwh ?? undefined,
  };
}

function mapTransaction(t: TransactionType): TransactionV2 {
  return {
    id: t._id,
    oderId: t.oderId || '',
    userId: t.userId || '',
    amount: t.amount / 100,
    bank: t.bank || '',
    invoiceId: t.invoiceId || '',
    tranId: t.tranId || '',
    status: toTransactionStatus(t.status),
    createdAt: t.createdAt,
  };
}

// Query keys for cache management.
// Only include server-relevant filter fields so that client-side-only filter
// changes (status, priceRange) don't cause unnecessary refetches.
export const dashboardQueryKeys = {
  stationRequests: (params: { page: number; pageSize: number; search: string }) =>
    [DashboardQueryKey.STATION_REQUESTS, params] as const,
  stations: (params: { page: number; pageSize: number; search: string; organizationId?: string }) =>
    [DashboardQueryKey.STATIONS, params] as const,
  station: (stationId: string) =>
    [DashboardQueryKey.STATIONS, 'detail', stationId] as const,
  stationsList: (search: string, organizationIds?: string) =>
    [DashboardQueryKey.STATION_OPTIONS, 'list', search, organizationIds ?? ''] as const,
  transactions: (params: { page: number; pageSize: number; filters: TransactionsFilterState }) =>
    [DashboardQueryKey.TRANSACTIONS, params] as const,
  sessions: (params: { page: number; pageSize: number; filters: SessionsFilterState }) =>
    [DashboardQueryKey.SESSIONS, params] as const,
  users: (params: {
    page: number;
    pageSize: number;
    search: string;
    role: string;
    status: string;
    dateRange: string;
    hasActiveSession: boolean;
    hasTelegram: boolean;
  }) => [DashboardQueryKey.USERS, params] as const,
  // Root key for invalidating every users-list query at once.
  usersRoot: () => [DashboardQueryKey.USERS] as const,
  userDetails: (userId: string) => [DashboardQueryKey.USERS, 'detail', userId] as const,
  userTransactions: (params: { userId: string; page: number; pageSize: number }) =>
    [DashboardQueryKey.TRANSACTIONS, 'by-user', params] as const,
  userSessions: (params: { userId: string; page: number; pageSize: number }) =>
    [DashboardQueryKey.SESSIONS, 'by-user', params] as const,
  dashboardStats: (params: { dateRange: string; stationIds?: string; organizationIds?: string }) =>
    [DashboardQueryKey.DASHBOARD_STATS, params] as const,
  kwhPerDay: (params: { dateRange: string; stationIds?: string; organizationIds?: string }) =>
    [DashboardQueryKey.DASHBOARD_KWH_PER_DAY, params] as const,
  stationOptions: () => [DashboardQueryKey.STATION_OPTIONS] as const,
  sessionMetrics: (sessionId: number) =>
    [DashboardQueryKey.SESSIONS, 'metrics', sessionId] as const,
  roles: () => [DashboardQueryKey.ROLES] as const,
  roleDetails: (roleId: string) =>
    [DashboardQueryKey.ROLES, 'detail', roleId] as const,
  permissions: () => [DashboardQueryKey.PERMISSIONS] as const,
  organizations: (params: { page: number; pageSize: number; search: string }) =>
    [DashboardQueryKey.ORGANIZATIONS, params] as const,
  // Root key for invalidating every organizations query (lists + details) at once.
  organizationsRoot: () => [DashboardQueryKey.ORGANIZATIONS] as const,
  organizationDetails: (id: string) =>
    [DashboardQueryKey.ORGANIZATIONS, 'detail', id] as const,
  organizationStations: (params: {
    organizationId: string;
    page: number;
    pageSize: number;
    search: string;
  }) => [DashboardQueryKey.ORGANIZATION_STATIONS, params] as const,
  myOrganization: () => [DashboardQueryKey.MY_ORGANIZATION] as const,
  ownerCandidates: (term: string) =>
    [DashboardQueryKey.USERS, 'owner-candidates', term] as const,
};

// Stations query hook
export function useStationsQuery(params: {
  page: number;
  pageSize: number;
  filters: StationsFilterState;
  enabled?: boolean;
}) {
  const { page, pageSize, filters, enabled = true } = params;

  return useQuery({
    queryKey: dashboardQueryKeys.stations({
      page,
      pageSize,
      search: filters.search,
      organizationId: filters.organizationId || undefined,
    }),
    queryFn: async (): Promise<{ data: StationV2[]; total: number }> => {
      const response = await ocppService.getDashboardStations({
        page,
        pageSize,
        sort: null,
        address: filters.search || undefined,
        organizationId: filters.organizationId || undefined,
      });

      return {
        data: response.data.map((s) => {
          const connectorPowers = (s.connectors as Array<{ power?: number }> | undefined)?.map(
            (c) => c.power ?? 0,
          ) ?? [];
          const totalPower = connectorPowers.reduce((sum, p) => sum + p, 0);
          return {
            id: s._id,
            idTag: s.idTag,
            option: s.option,
            status: toStationStatus(s.status),
            price: s.price,
            connectors: s.connectors?.length || 0,
            address: s.address || '',
            connectorTypes: (s as unknown as { connectorTypes?: string[] }).connectorTypes,
            power: totalPower > 0 ? totalPower : undefined,
            aroundTheClock: (s as unknown as { aroundTheClock?: boolean }).aroundTheClock,
            startWorkingTime:
              s.startWorkingTime instanceof Date
                ? s.startWorkingTime.toISOString()
                : (s.startWorkingTime as string | undefined),
            endWorkingTime:
              s.endWorkingTime instanceof Date
                ? s.endWorkingTime.toISOString()
                : (s.endWorkingTime as string | undefined),
            // Raw id string only — never reshape into an object, stationToFormData relies on this.
            organizationId: s.organizationId,
            organizationName: s.organizationName,
          };
        }),
        total: response.total,
      };
    },
    enabled,
    placeholderData: keepPreviousData,
  });
}

// Single station detail (StationDetails page).
// No automatic retry/polling: the page schedules its own bounded refetches
// after connector actions (OCPP roundtrip latency), see StationDetails.tsx.
export function useStationQuery(stationId: string | undefined) {
  return useQuery({
    queryKey: dashboardQueryKeys.station(stationId ?? ''),
    queryFn: async (): Promise<StationData> => {
      const response = await ocppService.getStation(stationId as string);
      return {
        id: response._id,
        idTag: response.idTag,
        status: toStationStatus(response.status),
        connectorTypes: (response.connectorTypes || []) as ConnectorTypeEnum[],
        option: response.option || 0,
        price: response.price || 0,
        address: response.address || '',
        latitude: response.latitude || 0,
        longitude: response.longitude || 0,
        aroundTheClock: response.aroundTheClock || false,
        startWorkingTime: response.startWorkingTime instanceof Date
          ? response.startWorkingTime.toISOString()
          : response.startWorkingTime,
        endWorkingTime: response.endWorkingTime instanceof Date
          ? response.endWorkingTime.toISOString()
          : response.endWorkingTime,
        currentLimit: response.currentLimit,
        loadBalancingStrategy: response.loadBalancingStrategy,
        downtimePricePerMinute: response.downtimePricePerMinute,
        downtimeFreeMinutes: response.downtimeFreeMinutes,
        organizationId: (response as { organizationId?: string }).organizationId,
        organizationName: response.organizationName,
        connectors: response.connectors?.map((c: { connectorId: number; status: string }, index: number) => ({
          id: c.connectorId || index + 1,
          status: toConnectorStatus(c.status),
          energy: 0,
          power: 0,
          current: 0,
          voltage: 0,
        })) || [{ id: 1, status: ConnectorStatus.AVAILABLE, energy: 0, power: 0, current: 0, voltage: 0 }],
      };
    },
    enabled: !!stationId,
    retry: false,
  });
}

// Transactions query hook
export function useTransactionsQuery(params: {
  page: number;
  pageSize: number;
  filters: TransactionsFilterState;
  enabled?: boolean;
}) {
  const { page, pageSize, filters, enabled = true } = params;

  return useQuery({
    queryKey: dashboardQueryKeys.transactions({ page, pageSize, filters }),
    queryFn: async (): Promise<{ data: TransactionV2[]; total: number }> => {
      const dateRange = getDateRangeFromPreset(filters.dateRange);
      const term = filters.search?.trim() || '';
      const isObjectId = /^[0-9a-fA-F]{24}$/.test(term);
      const response = await ocppService.getTransactions({
        page,
        pageSize,
        sort: null,
        email: !isObjectId && term ? term : undefined,
        userId: isObjectId ? term : undefined,
        status: filters.status || undefined,
        // UI amounts are in UAH; the backend filters stored minor units.
        minAmount: filters.minAmount > 0 ? Math.round(filters.minAmount * 100) : undefined,
        maxAmount: filters.maxAmount > 0 ? Math.round(filters.maxAmount * 100) : undefined,
        refundState: filters.refundState || undefined,
        ...dateRange,
      });

      return { data: response.data.map(mapTransaction), total: response.total };
    },
    enabled,
    placeholderData: keepPreviousData,
  });
}

// Sessions query hook
export function useSessionsQuery(params: {
  page: number;
  pageSize: number;
  filters: SessionsFilterState;
  enabled?: boolean;
}) {
  const { page, pageSize, filters, enabled = true } = params;

  return useQuery({
    queryKey: dashboardQueryKeys.sessions({ page, pageSize, filters }),
    queryFn: async (): Promise<{ data: SessionV2[]; total: number }> => {
      const dateRange = getDateRangeFromPreset(filters.dateRange);
      // Route the search term to the right backend filter: a 24-hex id is a
      // userId, an @-term is a user email, anything else is a station address.
      const term = filters.search?.trim() || '';
      const isObjectId = /^[0-9a-fA-F]{24}$/.test(term);
      const looksLikeEmail = term.includes('@');
      const response = await ocppService.getAllSessions({
        page,
        pageSize,
        sort: null,
        chargingStatus: filters.status || undefined,
        userId: isObjectId ? term : undefined,
        search: !isObjectId && term
          ? (looksLikeEmail ? { email: term } : { address: term })
          : undefined,
        // The station dropdown stores idTags; backend matches idTag $in.
        stationIdTags: filters.stationIds.length > 0 ? filters.stationIds.join(',') : undefined,
        // UI energy is in kWh; the backend compares meter values in Wh.
        maxEnergy: filters.maxEnergy > 0 ? Math.round(filters.maxEnergy * 1000) : undefined,
        maxDurationMinutes: filters.maxDurationMinutes > 0 ? filters.maxDurationMinutes : undefined,
        ...dateRange,
      });

      return { data: response.data.map(mapSession), total: response.total };
    },
    enabled,
    placeholderData: keepPreviousData,
    // Overrides the app-wide 5-minute staleTime / no-focus-refetch defaults
    // (see main.tsx) for this one list: sitting on it should show charging as
    // it happens, and coming back to the tab should not show a frozen table.
    staleTime: 0,
    refetchInterval: SESSIONS_POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });
}

// Per-session V/A/power time series
export function useSessionMetricsQuery(params: {
  sessionId: number | null;
  enabled?: boolean;
}) {
  const { sessionId, enabled = true } = params;

  return useQuery({
    queryKey: dashboardQueryKeys.sessionMetrics(sessionId ?? -1),
    queryFn: () => ocppService.getSessionMetrics(sessionId as number),
    enabled: enabled && sessionId != null && sessionId > 0,
    placeholderData: keepPreviousData,
  });
}

export function useSetChargingProfileLimitMutation() {
  return useMutation({
    mutationFn: (payload: { idTag: string; connectorId: number; amps: number }) =>
      ocppService.setChargingProfileLimit(payload),
  });
}

// Users query hook
export function useUsersQuery(params: {
  page: number;
  pageSize: number;
  filters: UsersFilterState;
  enabled?: boolean;
}) {
  const { page, pageSize, filters, enabled = true } = params;

  return useQuery({
    queryKey: dashboardQueryKeys.users({
      page,
      pageSize,
      search: filters.search,
      role: filters.role,
      status: filters.status,
      dateRange: filters.dateRange,
      hasActiveSession: filters.hasActiveSession,
      hasTelegram: filters.hasTelegram,
    }),
    queryFn: async (): Promise<{ data: UserV2[]; total: number }> => {
      const dateRange = filters.dateRange ? getDateRangeFromPreset(filters.dateRange) : {};
      const term = filters.search?.trim() || '';
      const looksLikeEmail = term.includes('@');
      const looksLikeId = /^[0-9a-fA-F]{24}$/.test(term);
      // Normalize phone: keep leading '+', strip all non-digit chars
      const normalizedPhone = term.replace(/[^\d+]/g, '').replace(/(?!^\+)\+/g, '');
      const response = await userService.getUsers(
        {
          email: looksLikeEmail ? term : undefined,
          userId: looksLikeId ? term : undefined,
          phone: !looksLikeEmail && !looksLikeId && normalizedPhone ? normalizedPhone : undefined,
          role: filters.role || undefined,
          status: filters.status || undefined,
          ...dateRange,
          hasActiveSession: filters.hasActiveSession || undefined,
          hasTelegram: filters.hasTelegram || undefined,
        },
        page,
        pageSize,
        null,
      );

      return {
        data: response.data.map((user) => ({
          id: user._id,
          email: user.email || '',
          phone: user.phone || '',
          status: toUserStatus(user.status),
          balance: (user.balance || 0) / 100,
          role: toUserRole(user.role),
          createdAt: user.createdAt || '',
          updatedAt: user.updatedAt || '',
        })),
        total: response.total,
      };
    },
    enabled,
    placeholderData: keepPreviousData,
  });
}

// Dashboard stats query hook
export function useDashboardStatsQuery(params: {
  dateRange: string;
  stationIds?: string;
  organizationIds?: string;
  enabled?: boolean;
}) {
  const { dateRange, stationIds, organizationIds, enabled = true } = params;

  return useQuery({
    queryKey: dashboardQueryKeys.dashboardStats({ dateRange, stationIds, organizationIds }),
    queryFn: async () => {
      const dateParams = getDateRangeFromPreset(dateRange);
      return ocppService.getDashboardStats({
        ...dateParams,
        stationIds,
        organizationIds,
      });
    },
    enabled,
  });
}

// Per-day kWh time series for the metrics chart.
// We pass the browser's IANA timezone so the backend buckets sessions by the
// user's local calendar day rather than UTC.
export function useKwhPerDayQuery(params: {
  dateRange: string;
  stationIds?: string;
  organizationIds?: string;
  enabled?: boolean;
}) {
  const { dateRange, stationIds, organizationIds, enabled = true } = params;

  return useQuery({
    queryKey: dashboardQueryKeys.kwhPerDay({ dateRange, stationIds, organizationIds }),
    queryFn: async () => {
      const dateParams = getDateRangeFromPreset(dateRange);
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return ocppService.getKwhPerDay({
        ...dateParams,
        stationIds,
        organizationIds,
        timezone,
      });
    },
    enabled,
  });
}

// Station list for the metrics multi-select (searchable, org-scoped).
// Keyed through the factory so CreateStation's STATION_OPTIONS invalidation
// also refreshes this dropdown.
export function useStationsListQuery(params: {
  search: string;
  organizationIds?: string;
  enabled?: boolean;
}) {
  const { search, organizationIds, enabled = true } = params;

  return useQuery({
    queryKey: dashboardQueryKeys.stationsList(search, organizationIds),
    queryFn: () => ocppService.getStationsList(search || undefined, organizationIds),
    enabled,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// Owner candidates for the organization owner picker: the term is routed to
// the email filter when it looks like an email, otherwise to the phone filter.
export function useOwnerCandidatesQuery(term: string) {
  const normalizedTerm = term.trim();

  return useQuery({
    queryKey: dashboardQueryKeys.ownerCandidates(normalizedTerm),
    queryFn: async (): Promise<OwnerCandidate[]> => {
      const looksLikeEmail = normalizedTerm.includes('@');
      const normalizedPhone = normalizedTerm.replace(/[^\d+]/g, '');
      const response = await userService.getUsers(
        {
          email: looksLikeEmail ? normalizedTerm : undefined,
          phone: !looksLikeEmail && normalizedPhone ? normalizedPhone : undefined,
        },
        0,
        OWNER_SEARCH_PAGE_SIZE,
        null,
      );
      return response.data.map((user) => ({
        id: user._id,
        email: user.email,
        phone: user.phone,
      }));
    },
    enabled: normalizedTerm.length > 0,
  });
}

// Station options for multi-select
export function useStationOptionsQuery(enabled = true) {
  return useQuery({
    queryKey: dashboardQueryKeys.stationOptions(),
    queryFn: async () => {
      const response = await ocppService.getDashboardStations({
        page: 1,
        pageSize: 100,
        sort: null,
      });
      return response.data.map((s) => ({
        id: s.idTag,
        label: s.address || s.idTag,
      }));
    },
    enabled,
    staleTime: 1000 * 60 * 10, // 10 minutes - stations don't change often
  });
}

// Single user detail query (admin)
export function useUserDetailsQuery(userId: string | undefined) {
  return useQuery({
    queryKey: dashboardQueryKeys.userDetails(userId ?? ''),
    queryFn: async (): Promise<UserDetailsV2> => {
      const raw = await userService.getUserById(userId as string);
      const roleIds = (raw.roleIds ?? []).map((r) =>
        typeof r === 'string' ? r : r?._id ?? r?.id ?? '',
      ).filter(Boolean);
      return {
        id: raw._id,
        email: raw.email || '',
        phone: raw.phone || '',
        status: toUserStatus(raw.status),
        // Backend stores balance in cents
        balance: (raw.balance ?? 0) / 100,
        role: toUserRole(raw.role),
        createdAt: raw.createdAt || '',
        updatedAt: raw.updatedAt || '',
        useBiometric: raw.useBiometric ?? null,
        roleIds,
        telegramChatId: raw.notification?.telegram_chat_id ?? null,
        organization: raw.organization ?? null,
        canJoinOrganization: raw.canJoinOrganization ?? false,
      };
    },
    enabled: !!userId,
  });
}

// Per-user paginated sessions
export function useUserSessionsQuery(params: {
  userId: string;
  page: number;
  pageSize: number;
  enabled?: boolean;
}) {
  const { userId, page, pageSize, enabled = true } = params;
  return useQuery({
    queryKey: dashboardQueryKeys.userSessions({ userId, page, pageSize }),
    queryFn: async (): Promise<{ data: SessionV2[]; total: number }> => {
      const response = await ocppService.getAllSessions({
        // The user-details panels page from 0; the endpoint is 1-based.
        page: page + 1,
        pageSize,
        sort: null,
        userId,
      });
      return { data: response.data.map(mapSession), total: response.total };
    },
    enabled: enabled && !!userId,
    placeholderData: keepPreviousData,
  });
}

// Per-user paginated transactions
export function useUserTransactionsQuery(params: {
  userId: string;
  page: number;
  pageSize: number;
  enabled?: boolean;
}) {
  const { userId, page, pageSize, enabled = true } = params;
  return useQuery({
    queryKey: dashboardQueryKeys.userTransactions({ userId, page, pageSize }),
    queryFn: async (): Promise<{ data: TransactionV2[]; total: number }> => {
      const response = await ocppService.getTransactions({
        // The user-details panels page from 0; the endpoint is 1-based.
        page: page + 1,
        pageSize,
        sort: null,
        userId,
      });
      return { data: response.data.map(mapTransaction), total: response.total };
    },
    enabled: enabled && !!userId,
    placeholderData: keepPreviousData,
  });
}

// Admin balance adjustment mutation
export function useUpdateUserBalanceMutation(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: BalanceUpdatePayload) =>
      userService.updateUserBalance(userId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: dashboardQueryKeys.userDetails(userId) });
      qc.invalidateQueries({ queryKey: dashboardQueryKeys.usersRoot() });
    },
  });
}

// Admin premium-account mutation
export function useUpdateUserStatusMutation(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (status: 'active' | 'inactive' | 'blocked') =>
      userService.updateUserStatus(userId, { status }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: dashboardQueryKeys.userDetails(userId) });
      qc.invalidateQueries({ queryKey: dashboardQueryKeys.usersRoot() });
    },
  });
}

// Merge a duplicate account into the survivor. Invalidates the survivor detail
// plus the users list and the sessions/transactions caches, since ownership of
// those rows moves to the survivor.
export function useMergeAccountsMutation(survivorId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (loserId: string) => userService.mergeAccounts(survivorId, loserId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: dashboardQueryKeys.userDetails(survivorId) });
      qc.invalidateQueries({ queryKey: [DashboardQueryKey.USERS] });
      qc.invalidateQueries({ queryKey: [DashboardQueryKey.SESSIONS] });
      qc.invalidateQueries({ queryKey: [DashboardQueryKey.TRANSACTIONS] });
    },
  });
}

// Organizations list (admin filter dropdown + admin page).
// Server-side pagination + search: returns { data, total }.
export function useOrganizationsQuery(
  page: number,
  pageSize: number,
  search = '',
  enabled = true,
) {
  return useQuery({
    queryKey: dashboardQueryKeys.organizations({ page, pageSize, search }),
    queryFn: () => organizationService.list(page, pageSize, search),
    enabled,
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
}

// The caller's own organization; null when they own none (404).
export function useMyOrganizationQuery(enabled = true) {
  return useQuery({
    queryKey: dashboardQueryKeys.myOrganization(),
    queryFn: async () => {
      try {
        return await organizationService.getMy();
      } catch (error) {
        if (parseApiError(error).statusCode === 404) {
          return null;
        }
        throw error;
      }
    },
    enabled,
  });
}

// One organization by id (admin create/edit page).
export function useOrganizationQuery(id: string | undefined) {
  return useQuery({
    queryKey: dashboardQueryKeys.organizationDetails(id ?? ''),
    queryFn: () => organizationService.get(id as string),
    enabled: !!id,
  });
}

export function useCreateOrganizationMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateOrganizationPayload) =>
      organizationService.create(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: dashboardQueryKeys.organizationsRoot() });
    },
  });
}

export function useUpdateOrganizationMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateOrganizationPayload }) =>
      organizationService.update(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: dashboardQueryKeys.organizationsRoot() });
    },
  });
}

// Stations belonging to one organisation (the org stations page).
// Maps the raw StationTypes rows to StationV2, mirroring useStationsQuery above —
// the next task's page keys rows on `id` and reads `organizationName` for display.
export function useOrganizationStationsQuery(
  organizationId: string,
  page: number,
  pageSize: number,
  search = '',
) {
  return useQuery({
    queryKey: dashboardQueryKeys.organizationStations({
      organizationId, page, pageSize, search,
    }),
    queryFn: async (): Promise<{ data: StationV2[]; total: number }> => {
      const response = await ocppService.getStationsByOrganization(
        organizationId, page, pageSize, search,
      );

      return {
        data: response.data.map((s) => {
          const connectorPowers = (s.connectors as Array<{ power?: number }> | undefined)?.map(
            (c) => c.power ?? 0,
          ) ?? [];
          const totalPower = connectorPowers.reduce((sum, p) => sum + p, 0);
          return {
            id: s._id,
            idTag: s.idTag,
            option: s.option,
            status: toStationStatus(s.status),
            price: s.price,
            connectors: s.connectors?.length || 0,
            address: s.address || '',
            connectorTypes: (s as unknown as { connectorTypes?: string[] }).connectorTypes,
            power: totalPower > 0 ? totalPower : undefined,
            aroundTheClock: (s as unknown as { aroundTheClock?: boolean }).aroundTheClock,
            startWorkingTime:
              s.startWorkingTime instanceof Date
                ? s.startWorkingTime.toISOString()
                : (s.startWorkingTime as string | undefined),
            endWorkingTime:
              s.endWorkingTime instanceof Date
                ? s.endWorkingTime.toISOString()
                : (s.endWorkingTime as string | undefined),
            // Raw id string only — never reshape into an object, stationToFormData relies on this.
            organizationId: s.organizationId,
            organizationName: s.organizationName,
          };
        }),
        total: response.total,
      };
    },
    enabled: Boolean(organizationId),
    placeholderData: keepPreviousData,
  });
}

// Attach (id) or detach (null) a station's organisation.
export function useAssignStationOrganizationMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      stationId,
      organizationId,
    }: {
      stationId: string;
      organizationId: string | null;
    }) => ocppService.updateStation(stationId, { organizationId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [DashboardQueryKey.ORGANIZATION_STATIONS] });
      qc.invalidateQueries({ queryKey: [DashboardQueryKey.STATIONS] });
      // The Organizations table shows a station count that just changed.
      qc.invalidateQueries({ queryKey: [DashboardQueryKey.ORGANIZATIONS] });
    },
  });
}

// All stations, searched server-side — the picker on the org stations page.
// `address` is the station list's free-text parameter; StationQueryBuilder
// matches it against address, idTag and numeric code.
export function useStationSearchQuery(search: string, enabled = true) {
  return useQuery({
    queryKey: [DashboardQueryKey.STATIONS, 'search', search],
    queryFn: async (): Promise<{ data: StationV2[]; total: number }> => {
      const response = await ocppService.getStations({
        page: 1,
        pageSize: 20,
        sort: null,
        address: search || undefined,
      });

      return {
        data: response.data.map((s) => ({
          id: s._id,
          idTag: s.idTag,
          option: s.option,
          status: toStationStatus(s.status),
          price: s.price,
          connectors: s.connectors?.length || 0,
          address: s.address || '',
          // Raw id string only — never reshape into an object.
          organizationId: s.organizationId,
          organizationName: s.organizationName,
        })),
        total: response.total,
      };
    },
    enabled,
  });
}

export function useSetMonobankTokenMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (token: string) => organizationService.setMonobankToken(token),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: dashboardQueryKeys.myOrganization() });
      qc.invalidateQueries({ queryKey: dashboardQueryKeys.organizationsRoot() });
    },
  });
}

export function useRemoveMonobankTokenMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => organizationService.removeMonobankToken(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: dashboardQueryKeys.myOrganization() });
      qc.invalidateQueries({ queryKey: dashboardQueryKeys.organizationsRoot() });
    },
  });
}

// Roles list
export function useRolesQuery(enabled = true) {
  return useQuery({
    queryKey: dashboardQueryKeys.roles(),
    queryFn: () => roleService.listRoles(),
    enabled,
  });
}

// Single role (for edit)
export function useRoleQuery(roleId: string | undefined) {
  return useQuery({
    queryKey: roleId ? dashboardQueryKeys.roleDetails(roleId) : ['role', 'none'],
    queryFn: () => roleService.getRole(roleId as string),
    enabled: !!roleId,
  });
}

// Permission catalog grouped
export function usePermissionsQuery(enabled = true) {
  return useQuery({
    queryKey: dashboardQueryKeys.permissions(),
    queryFn: () => roleService.listPermissions(),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateRoleMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateRolePayload) => roleService.createRole(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: dashboardQueryKeys.roles() });
    },
  });
}

export function useUpdateRoleMutation(roleId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateRolePayload) =>
      roleService.updateRole(roleId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: dashboardQueryKeys.roles() });
      qc.invalidateQueries({ queryKey: dashboardQueryKeys.roleDetails(roleId) });
    },
  });
}

export function useDeleteRoleMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ roleId, options }: { roleId: string; options?: DeleteRoleOptions }) =>
      roleService.deleteRole(roleId, options),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: dashboardQueryKeys.roles() });
      qc.invalidateQueries({ queryKey: dashboardQueryKeys.usersRoot() });
    },
  });
}

// Replace a user's role assignments. Invalidates that user's detail cache
// and the users list so the new role surfaces immediately.
export function useAssignUserRolesMutation(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: AssignRolesPayload) =>
      roleService.assignUserRoles(userId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: dashboardQueryKeys.userDetails(userId) });
      qc.invalidateQueries({ queryKey: dashboardQueryKeys.usersRoot() });
      qc.invalidateQueries({ queryKey: dashboardQueryKeys.roles() });
    },
  });
}

// Station requests query hook (admin)
export function useStationRequestsQuery(params: {
  page: number;
  pageSize: number;
  filters: StationRequestsFilterState;
  enabled?: boolean;
}) {
  const { page, pageSize, filters, enabled = true } = params;
  return useQuery({
    queryKey: dashboardQueryKeys.stationRequests({ page, pageSize, search: filters.search }),
    queryFn: async (): Promise<{ data: StationRequestV2[]; total: number }> => {
      const response = await ocppService.getStationRequests({
        page,
        pageSize,
        search: filters.search || undefined,
      });
      return {
        data: response.data.map((r) => {
          const user = r.userId && typeof r.userId === 'object' ? r.userId : null;
          return {
            id: r._id,
            city: r.city,
            phone: r.phone,
            comment: r.comment,
            userId: user ? user._id : (typeof r.userId === 'string' ? r.userId : undefined),
            userEmail: user?.email,
            userPhone: user?.phone,
            createdAt: r.createdAt,
          };
        }),
        total: response.total,
      };
    },
    enabled,
    placeholderData: keepPreviousData,
  });
}
